import { ApiError } from '@lista/api-client';
import type { ErrorCode, ProblemDetails } from '@lista/shared-types';

export { ApiError };

/** The request never reached the API (offline, DNS, TLS, aborted). */
export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super('Network request failed', { cause });
    this.name = 'NetworkError';
  }
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;
export const isNetworkError = (e: unknown): e is NetworkError => e instanceof NetworkError;

export function errorCode(e: unknown): ErrorCode | undefined {
  return isApiError(e) ? e.problem.code : undefined;
}

export function errorStatus(e: unknown): number | undefined {
  return isApiError(e) ? e.problem.status : undefined;
}

/** Field errors from a 400 problem, keyed by path (`name`, `email`, …). */
export function fieldErrors(e: unknown): Record<string, string> {
  if (!isApiError(e) || !e.problem.errors) return {};
  const out: Record<string, string> = {};
  for (const err of e.problem.errors) out[err.path || '_'] ??= err.message;
  return out;
}

/**
 * Codes for failures that never reached the API's problem filter (a proxy's HTML 413 or 502,
 * say). Mirrors the API's own status mapping; anything else is generic, never "not found".
 */
const CODE_BY_STATUS: Partial<Record<number, ErrorCode>> = {
  400: 'VALIDATION_FAILED',
  401: 'UNAUTHENTICATED',
  403: 'FORBIDDEN',
  404: 'NOT_FOUND',
  409: 'CONFLICT',
  413: 'VALIDATION_FAILED',
  429: 'RATE_LIMITED',
};

/** Wraps a problem body (or a non-JSON failure) into an ApiError. */
export function toApiError(body: unknown, response: Response): ApiError {
  if (body && typeof body === 'object' && 'code' in body && 'status' in body) {
    return new ApiError(body as ProblemDetails);
  }
  return new ApiError({
    type: 'about:blank',
    title: response.statusText || 'Request failed',
    status: response.status,
    code: CODE_BY_STATUS[response.status] ?? 'INTERNAL_ERROR',
  });
}

/** i18n key for an error: `errors.codes.<CODE>`, `errors.network` or `errors.generic`. */
export function errorMessageKey(e: unknown, hasKey: (key: string) => boolean): string {
  if (isNetworkError(e)) return 'errors.network';
  const code = errorCode(e);
  if (code && hasKey(`errors.codes.${code}`)) return `errors.codes.${code}`;
  return 'errors.generic';
}
